import React from 'react';
import { IRoute, Material } from '../../sp-types';
import { useDrag, useDrop } from 'react-dnd';
import { MenuOutlined } from '@mui/icons-material';
import { BalanceRouteMetricCard } from '@components/RouteCards/BalanceRouteMetricCard';
import { routeToBalanceRouteCardProps } from '@components/RouteCards/utils';

const ItemType = {
  ROUTE: 'ROUTE',
};

interface routeProps {
  route: IRoute;
  index: number;
  listType: 'all' | 'selected';
  showPopup: (_route: IRoute) => void;
  reorderRoute: (_dragIndex: number, _hoverIndex: number) => void;
  material: Material;
}
export const DraggableRoute: React.FC<routeProps> = ({ route, index, listType, showPopup, reorderRoute, material }) => {
  const [{ isDragging }, dragRef] = useDrag({
    type: ItemType.ROUTE,
    item: { route, index, listType },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, dropRef] = useDrop({
    accept: ItemType.ROUTE,
    hover: (item: { index: number; listType: string }) => {
      if (item.listType === listType && item.index !== index) {
        reorderRoute(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => dragRef(dropRef(node))}
      className={`flex gap-2 -ml-2 ${isDragging ? 'opacity-50' : ''}`}
      key={index}
      onDoubleClick={() => showPopup(route)}
    >
      <div className="flex flex-col cursor-move justify-center">
        <MenuOutlined style={{ fill: '#9f9f9f' }} />
      </div>
      <BalanceRouteMetricCard
        {...routeToBalanceRouteCardProps(route, () => {}, route.materialId, index + 1 + '.', material, undefined)}
        isMinimalCell
      />
    </div>
  );
};

interface DroppableColumnProps {
  routes: IRoute[];
  onChangeRoutes: (_routes: IRoute[], _listType: 'all' | 'selected') => void;
  listType: 'all' | 'selected';
  otherList: IRoute[];
  showPopup: (_route: IRoute) => void;
  title?: string;
  dropMessage?: string;
  materialsMap: Record<string, Material>;
}

export const DroppableList: React.FC<DroppableColumnProps> = ({
  routes,
  listType,
  otherList,
  onChangeRoutes,
  showPopup,
  title,
  dropMessage,
  materialsMap,
}) => {
  const [, dropRef] = useDrop({
    accept: ItemType.ROUTE,
    drop: (item: { route: IRoute; index: number; listType: string }) => {
      if (item.listType !== listType) {
        const draggedRoute = otherList[item.index];

        // Remove from source list
        const updatedSourceList = otherList.filter((_, idx) => idx !== item.index);
        onChangeRoutes(updatedSourceList, listType === 'all' ? 'selected' : 'all');

        // Add to target list
        onChangeRoutes([...routes, draggedRoute], listType);
      }
    },
  });

  const reorderRoute = (dragIndex: number, hoverIndex: number) => {
    const updatedRoutes = [...routes];
    const [movedRoute] = updatedRoutes.splice(dragIndex, 1);
    updatedRoutes.splice(hoverIndex, 0, movedRoute);
    onChangeRoutes(
      updatedRoutes.map((route, index) => ({ ...route, order: index })),
      listType,
    );
  };
  return (
    <div ref={dropRef} className="flex flex-col flex-1 gap-3 bg-white p-3">
      {title && <div className="text-red pl-6">{title}</div>}
      {routes.length > 0 ? (
        routes
          .map((route) => ({ ...route, material: materialsMap[route.materialId] }))
          .map(({ material, ...route }, index) => (
            <DraggableRoute key={route.id} {...{ route, index, listType, showPopup, reorderRoute, material }} />
          ))
      ) : (
        <div className="flex flex-1 justify-center p-4 border-y min-w-[30vw] mx-auto border-gray-125">
          No routes availble
        </div>
      )}
      {dropMessage && <DropArea title={dropMessage} />}
    </div>
  );
};

export const DropArea: React.FC<{ title: string }> = ({ title }) => (
  <div className="flex flex-col h-22 p-4 ms-4 gap-3 items-center justify-center border-2 border-dashed  border-purple-100">
    <img src="/assets/icons/flat/tap.svg" alt="" />
    <div>{title}</div>
  </div>
);
